/**
 * Back office → Academy → « Learners' reviews » (D-142, action 5): nothing is published without a
 * human decision; a review is published, refused (with an internal reason) or cleared of personal
 * data only (the server checks that no word is added or changed, and the site says so). Never an
 * invented review.
 */

import { type TestimonialAdmin, useAcademyTestimonialsList, useAcademyTestimonialsModerateCreate } from "@agencyos/api-client";
import { stars } from "@agencyos/core";
import { Badge, type BadgeTone, Button, GlassCard, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAdminExam } from "./examContext";
import { ErrorList, Pager, useAcademyRoles, useRefreshAcademy } from "./shared";

const TONES: Record<string, BadgeTone> = { pending: "warning", published: "success", rejected: "danger", withdrawn: "default" };
const STATUSES = ["pending", "published", "rejected", "withdrawn"] as const;

function ReviewRow({ row, canModerate }: { row: TestimonialAdmin; canModerate: boolean }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshAcademy();
  const moderate = useAcademyTestimonialsModerateCreate({ mutation: { onSuccess: refresh } });
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(row.text);
  const consent = new Date(row.consent_at).toLocaleDateString(i18n.language.startsWith("en") ? "en-GB" : "fr-FR");
  return (
    <GlassCard as="article" className="stack">
      <div className="row between">
        <span>
          <strong>{row.display_name}</strong> · {row.country} · <span aria-label={t("coaching.reviews.ratingValue", { count: row.rating })}>{stars(row.rating)}</span> ·{" "}
          {t(`coaching.reviews.occasion.${row.source}`)}
        </span>
        <Badge tone={TONES[row.status] ?? "default"}>{t(`coaching.admin.reviews.${row.status}`)}</Badge>
      </div>
      <blockquote className="m-0" lang={row.language}>
        « {row.text} »
      </blockquote>
      {row.redacted ? (
        <p className="muted academy-small">
          {t("coaching.reviews.redacted")} — {t("coaching.admin.reviews.original")} : « {row.original_text} »
        </p>
      ) : null}
      <p className="muted academy-small">{t("coaching.admin.reviews.consent", { date: consent, version: row.consent_version })}</p>
      {canModerate && row.status !== "withdrawn" ? (
        <div className="stack">
          <div className="row">
            {row.status !== "published" ? (
              <Button disabled={moderate.isPending} onClick={() => moderate.mutate({ id: row.id, data: { action: "publish" } })}>
                {t("coaching.admin.reviews.publish")}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setEditing((v) => !v)}>
              {t("coaching.admin.reviews.redact")}
            </Button>
          </div>
          {editing ? (
            <div className="stack">
              <p className="muted academy-small">{t("coaching.admin.reviews.redactHint")}</p>
              <TextAreaField label={t("coaching.reviews.text")} rows={4} value={text} onChange={(e) => setText(e.target.value)} />
              <div className="row">
                <Button variant="secondary" disabled={moderate.isPending} onClick={() => moderate.mutate({ id: row.id, data: { action: "redact", text } }, { onSuccess: () => setEditing(false) })}>
                  {t("coaching.admin.reviews.redactSave")}
                </Button>
              </div>
            </div>
          ) : null}
          {row.status !== "rejected" ? (
            <div className="row">
              <TextField label={t("coaching.admin.reviews.rejectNote")} value={note} onChange={(e) => setNote(e.target.value)} />
              <Button variant="ghost" disabled={moderate.isPending || !note.trim()} onClick={() => moderate.mutate({ id: row.id, data: { action: "reject", note } })}>
                {t("coaching.admin.reviews.reject")}
              </Button>
            </div>
          ) : null}
          <ErrorList error={moderate.error} />
        </div>
      ) : null}
    </GlassCard>
  );
}

export function ReviewsTab() {
  const { t } = useTranslation();
  const { role } = useAcademyRoles();
  const canModerate = role === "owner" || role === "manager";
  const [status, setStatus] = useState<string>("pending");
  const [page, setPage] = useState(1);
  const { params: examFilter } = useAdminExam();
  const list = useAcademyTestimonialsList({ page, ...(status ? { status } : {}), ...examFilter });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <div className="stack">
      <GlassCard as="section" className="stack" aria-labelledby="reviews-title">
        <div className="section-title">
          <div>
            <h2 id="reviews-title">{t("coaching.admin.reviewsTab")}</h2>
            <p className="muted">{t("coaching.admin.reviews.rule")}</p>
          </div>
          <SelectField
            label={t("academyLive.admin.statusLabel")}
            value={status}
            options={[...STATUSES.map((s) => ({ value: s, label: t(`coaching.admin.reviews.${s}`) })), { value: "", label: t("coaching.admin.all") }]}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          />
        </div>
        {data === undefined ? (
          <Skeleton style={{ height: 120 }} />
        ) : data.results.length ? (
          <>
            {data.results.map((row) => (
              <ReviewRow key={row.id} row={row} canModerate={canModerate} />
            ))}
            <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
          </>
        ) : (
          <p className="muted">{t("coaching.admin.reviews.none")}</p>
        )}
      </GlassCard>
    </div>
  );
}
